import React, { memo, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { ParamsProps } from 'src/app/common/components/ParamsProvider';
import { dataConvert, useTargetApplicant } from './target-applicant.hook';
import TargetApplicationSection from 'src/app/common/components/TargetApplicantSection';
import { ErrorState } from 'src/app/common/utils/form-error-utils';
import { EnrolmentType, WalletAwardFormMode } from 'src/app/modules/pruwallet/types/award-type';
import { WalletAwardFormAction, WalletAwardFormState } from '../AwardDetail.util';
import { DataUploaderComponent } from 'src/app/common/components/data-uploader/data-uploader.component';
import { fetchDesignationAndZone } from 'src/app/common/network';

export type TargetApplicantProps = ParamsProps & {
  enrolmentType: EnrolmentType;
  modifyTargetApplicant: (
    option: 'enrolmentType' | 'eligibleDesignationsHK' | 'eligibleDesignationsMO' | 'agentListUploaded',
    value: any,
  ) => void;
  formState: WalletAwardFormState;
  formDispatch: React.Dispatch<WalletAwardFormAction>;
  formMode: WalletAwardFormMode;
  errorState?: ErrorState;
  disabled?: boolean;
  disabledByDesignation?: boolean;
  disabledByUploadAgent?: boolean;
};

export const TargetApplicantComponent: React.FC<TargetApplicantProps> = memo((props: TargetApplicantProps) => {
  const {
    modifyTargetApplicant,
    enrolmentType,
    formState,
    disabled = false,
    disabledByDesignation = false,
    disabledByUploadAgent = false,
  } = props;
  // i18n
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  const [isDataReady, setIsDataReady] = useState(false);

  useEffect(() => {
    setIsDataReady(false);
    fetchDesignationAndZone().finally(() => {
      setIsDataReady(true);
    });
  }, []);

  // custom-hook
  const { isShowAgentDownload, getUploadedAgents, validateUploadedData } = useTargetApplicant(props);
  return (
    <>
      <FormControl>
        <RadioGroup
          aria-label="receiverType"
          aria-disabled={disabled}
          name="receiverType"
          value={enrolmentType}
          onChange={(e) => {
            modifyTargetApplicant('enrolmentType', e.target.value);
          }}
          className="flex-row tw-mb-2"
        >
          <FormControlLabel
            key="by-segement"
            value={EnrolmentType.BY_SEGMENT}
            control={<Radio disabled={disabled || disabledByDesignation} />}
            label={t('targetApplicantSection.common.target_applicant_type.designations')}
          />

          <FormControlLabel
            key="target-reveivers"
            value={EnrolmentType.SPECIFIC}
            control={<Radio disabled={disabled || disabledByUploadAgent} />}
            label={t('resource_targetType_byList')}
          />
        </RadioGroup>
      </FormControl>
      {isDataReady && (
        <>
          <div className={formState.enrolmentType === EnrolmentType.BY_SEGMENT ? '' : 'tw-hidden'}>
            <TargetApplicationSection
              isDisabled={disabled || disabledByDesignation}
              // reminderText={t('walletAward.common.designationReminder')}
              designationText={t('walletAward.common.designationTextHK')}
              eligibleZones={[]}
              enableZones={false}
              isNew={false} // no selecting all Designation by default
              eligibleDesignations={formState.eligibleDesignationsHK || []}
              eligibleAgents={[]}
              excludedAgents={[]}
              onToggleDesignation={(value) => {
                modifyTargetApplicant('eligibleDesignationsHK', value);
              }}
            />
            <TargetApplicationSection
              isDisabled={disabled || disabledByDesignation}
              designationText={t('walletAward.common.designationTextMO')}
              eligibleZones={[]}
              enableZones={false}
              isNew={false}
              eligibleDesignations={formState.eligibleDesignationsMO || []}
              eligibleAgents={[]}
              excludedAgents={[]}
              onToggleDesignation={(value) => {
                modifyTargetApplicant('eligibleDesignationsMO', value);
              }}
            />
          </div>
        </>
      )}
      <div className={formState.enrolmentType === EnrolmentType.SPECIFIC ? '' : 'tw-hidden'}>
        <DataUploaderComponent
          onChange={(data) => {
            modifyTargetApplicant('agentListUploaded', dataConvert(data));
          }}
          validateData={validateUploadedData}
          onDownload={isShowAgentDownload ? getUploadedAgents : undefined}
          displayFileName={'upload-agent-list'}
          templateData={sampleData}
          disabled={disabled}
        />
      </div>
    </>
  );
});
// for download-template excel
const sampleData = [{ agentCode: '00010001' }, { agentCode: '00010002' }];
